import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import ArrowDropDown from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUp from '@mui/icons-material/ArrowDropUpRounded';
import InsertEmoticonOutlinedIcon from '@mui/icons-material/InsertEmoticonOutlined';
import ButtonBase from '@mui/material/ButtonBase';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import { emojiFromString } from 'containers/SpendingWatchlist/utils';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(({ spacing, palette, shape, typography }, { popoverEl }, classes) => {
  const focusInputStyle = {
    borderColor: `${palette.primary.main} !important`,
    borderWidth: '2px !important',
  };

  const normalInputStyle = {
    borderColor: `${palette.border} !important`,
    borderWidth: '1px !important',
  };

  return {
    root: {
      position: 'relative',
      minWidth: 78,
      marginTop: spacing(2),
      outline: 'none',
      '& *': {
        cursor: 'pointer !important',
      },
    },
    invisibleEl: {
      width: 0,
      height: 0,
      outline: 'none',
    },
    emojiRoot: {
      position: 'absolute',
      left: 0,
      top: 28,
      zIndex: 2,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      transform: 'translateY(-50%)',
      padding: '2px 16px 0',
      pointerEvents: 'none',
      '& button': {
        width: 32,
        height: 32,
        backgroundColor: palette.background.default,
        borderRadius: shape.borderRadius,
        padding: 4,
      },
      '& em-emoji': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        ...typography.headlineRegular,
        textAlign: 'center',
      },
    },
    popoverPaper: {
      transform: 'translateX(-12px) !important;',
    },
    overwriteInput: {
      '& fieldset': popoverEl ? focusInputStyle : normalInputStyle,
      '&:hover fieldset': {
        borderColor: `${palette.text.primary} !important`,
      },
    },
    overwriteFocus: {
      '& fieldset': popoverEl ? normalInputStyle : focusInputStyle,
    },
    inputError: {
      [`&.${classes.overwriteInput} fieldset`]: {
        borderColor: `${palette.error.main} !important`,
      },
      [`&.${classes.overwriteInput}:hover fieldset`]: {
        borderColor: `${palette.error.main} !important`,
      },
    },
    adornedEnd: {
      paddingRight: 7,
    },
    adornedEndIcon: {
      color: palette.action.active,
    },
    placeholderIcon: {
      width: 20,
      height: 20,
      color: palette.text.secondary,
    },
  };
});

const anchorOrigin = {
  vertical: 'center',
  horizontal: 'right',
};
const transformOrigin = {
  vertical: 'top',
  horizontal: 'right',
};

const InputLabelProps = { shrink: true };

const QEmojiSelect = (props) => {
  const { id, label, name, value, error, helperText, onChange, onBlur } = props;
  const [popoverEl, setPopoverEl] = useState(null);

  const { classes } = useStyles({ popoverEl });

  const invisibleElRef = useRef();
  const selectRef = useRef();
  const popoverElRef = useRef();

  const onOpenPicker = useCallback(() => {
    setPopoverEl(popoverElRef.current);
  }, [popoverElRef]);

  const onClosePopover = useCallback(() => {
    setPopoverEl(null);
    setTimeout(() => invisibleElRef.current?.focus(), 0);
  }, [invisibleElRef]);

  const onSelect = useCallback(
    (emoji) => {
      if (emoji) {
        onChange(`U+${emoji.unified}`);
      }
      onClosePopover(true);
    },
    [onChange, onClosePopover],
  );

  const onTextFieldClick = useCallback(() => {
    if (!popoverEl) {
      onOpenPicker();
      selectRef.current.blur();
    }
  }, [popoverEl, onOpenPicker, selectRef]);

  const inputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment className={classes.adornedEndIcon} disablePointerEvents position='end'>
          {popoverEl ? <ArrowDropUp /> : <ArrowDropDown />}
        </InputAdornment>
      ),
      autoComplete: 'off',
      readOnly: true,
      classes: {
        root: classes.overwriteInput,
        focused: classes.overwriteFocus,
        adornedEnd: classes.adornedEnd,
        error: classes.inputError,
      },
    }),
    [classes, popoverEl],
  );

  const emojiData = useMemo(() => emojiFromString(value), [value]);

  useEffect(() => {
    // Disable tab selection on emoji button
    const emojiButton = popoverElRef.current.querySelector('button');
    if (emojiButton) {
      emojiButton.tabIndex = -1;
    }
  }, [popoverElRef]);

  return (
    <div className={classes.root}>
      <TextField
        inputRef={selectRef}
        id={id}
        label={label}
        name={name}
        error={error}
        helperText={helperText}
        InputProps={inputProps}
        InputLabelProps={InputLabelProps}
        onClick={onTextFieldClick}
        onBlur={onBlur}
        variant='filled'
        fullWidth
      />
      <div ref={popoverElRef} className={classes.emojiRoot}>
        {emojiData ? (
          <em-emoji
            size={24}
            native={emojiData.emoji}
            set={'native'}
            skin={emojiData.skinToneIndex}
            onClick={onOpenPicker}
          />
        ) : (
          <ButtonBase tabIndex={-1}>
            <InsertEmoticonOutlinedIcon className={classes.placeholderIcon} />
          </ButtonBase>
        )}
      </div>
      <Popover
        id='emoji-picker-popover'
        open={Boolean(popoverEl)}
        anchorEl={popoverEl}
        onClose={onClosePopover}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        classes={{
          paper: classes.popoverPaper,
        }}
      >
        <Picker onEmojiSelect={onSelect} data={data} title='Pick your emoji…' emoji='point_up' native />
      </Popover>
      <div className={classes.invisibleEl} ref={invisibleElRef} tabIndex={-1} />
    </div>
  );
};

QEmojiSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

QEmojiSelect.whyDidYouRender = true;

export default QEmojiSelect;
